import { useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock } from 'lucide-react';
import { Button, TextField } from '@tappa/ui';
import { ApiError, api } from '../../lib/api.js';

/**
 * Orari di **questa** giornata.
 *
 * È il comando che mancava, e la sua assenza rendeva incomprensibile quello a
 * livello di viaggio: le impostazioni del viaggio valgono come predefinite per
 * le giornate nuove, ma non toccano quelle già create. Chi cambiava l'orario lì
 * e non vedeva succedere niente concludeva, giustamente, che fosse rotto.
 *
 * Qui invece si agisce sul giorno che si sta guardando, ed è il valore che il
 * planner legge davvero quando ricostruisce la giornata.
 */
export function DayHours({
  tripId,
  dayId,
  inizio,
  fine,
  canEdit,
}: {
  tripId: string;
  dayId: string;
  inizio: string;
  fine: string;
  canEdit: boolean;
}): ReactNode {
  const queryClient = useQueryClient();
  const [aperto, setAperto] = useState(false);
  const [dalle, setDalle] = useState(inizio.slice(0, 5));
  const [alle, setAlle] = useState(fine.slice(0, 5));
  const [errore, setErrore] = useState<string | null>(null);

  const salva = useMutation({
    mutationFn: () => api.days.update(tripId, dayId, { dayStartLocal: dalle, dayEndLocal: alle }),
    onSuccess: async () => {
      setErrore(null);
      setAperto(false);
      await queryClient.invalidateQueries({ queryKey: ['day'] });
      await queryClient.invalidateQueries({ queryKey: ['today'] });
    },
    onError: (error) => {
      setErrore(error instanceof ApiError ? error.message : 'Non sono riuscito a salvare.');
    },
  });

  if (!aperto) {
    return (
      <button
        type="button"
        disabled={!canEdit}
        onClick={() => {
          setAperto(true);
        }}
        className="inline-flex min-h-[36px] items-center gap-1.5 rounded-[var(--r-full)] px-2 text-[13.5px] text-text-muted hover:bg-surface-sunken disabled:hover:bg-transparent"
      >
        <Clock size={14} aria-hidden="true" />
        <span className="tabular">
          {inizio.slice(0, 5)} – {fine.slice(0, 5)}
        </span>
      </button>
    );
  }

  const valido = dalle !== '' && alle !== '' && dalle < alle;

  return (
    <div className="rounded-[var(--r-md)] bg-surface-sunken p-3">
      <p className="mb-2 text-[13.5px] text-text-muted">
        Entro questa fascia il planner costruisce la giornata. Dopo averla cambiata,
        rigenera la giornata per applicarla.
      </p>

      <div className="flex gap-2">
        <div className="flex-1">
          <TextField
            label="Dalle"
            type="time"
            value={dalle}
            onChange={(event) => {
              setDalle(event.target.value);
            }}
          />
        </div>
        <div className="flex-1">
          <TextField
            label="Alle"
            type="time"
            value={alle}
            onChange={(event) => {
              setAlle(event.target.value);
            }}
          />
        </div>
      </div>

      {!valido && dalle !== '' && alle !== '' ? (
        <p className="mt-1 text-[13px] text-[var(--budget-warn)]">
          L’ora di fine deve venire dopo quella di inizio.
        </p>
      ) : null}

      {errore !== null ? (
        <p role="alert" className="mt-1 text-[13px] text-[var(--budget-blocked)]">
          {errore}
        </p>
      ) : null}

      <div className="mt-2 flex gap-2">
        <Button
          disabled={!valido}
          loading={salva.isPending}
          onClick={() => {
            salva.mutate();
          }}
        >
          Salva
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            setDalle(inizio.slice(0, 5));
            setAlle(fine.slice(0, 5));
            setAperto(false);
            setErrore(null);
          }}
        >
          Annulla
        </Button>
      </div>
    </div>
  );
}
